import { useState, useCallback, Fragment } from 'react';
import api from '../../api/client';
import toast from 'react-hot-toast';
import StaffLink from '../../components/shared/StaffLink';
import {
  BanknotesIcon, ArrowDownTrayIcon, MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { DATE_FLOOR } from '../../lib/dateBounds';

const formatHours = (mins) => {
  if (!mins) return '0';
  const h = mins / 60;
  return h.toFixed(2);
};

// Sensible default range: this week (Mon–Sun) — most centers run weekly payroll.
function defaultRange() {
  const today = new Date();
  const dow = today.getDay();
  const back = dow === 0 ? 6 : dow - 1; // back to Mon
  const monday = new Date(today);
  monday.setDate(monday.getDate() - back);
  const sunday = new Date(monday);
  sunday.setDate(sunday.getDate() + 6);
  const fmt = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  return { from: fmt(monday), to: fmt(sunday) };
}

export default function PayrollPage() {
  const initRange = defaultRange();
  const [from, setFrom] = useState(initRange.from);
  const [to, setTo] = useState(initRange.to);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/payroll/preview', { params: { from, to } });
      setData(data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load preview');
    } finally { setLoading(false); }
  }, [from, to]);

  const downloadCSV = () => {
    const url = `/api/payroll/export.csv?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    window.open(url, '_blank');
  };

  const toggleStaff = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="animate-slide-up">
      <header className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BanknotesIcon className="w-6 h-6" /> Payroll
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            <strong>Actual clocked hours</strong> per staff for any date range, then export as
            CSV. Hours are split into <strong>Until 6 PM</strong> and <strong>After 6 PM</strong>{' '}
            (the +$1.50/hr differential) — payroll applies the rate. Paid holidays are shown
            separately. A shift with a missing clock-out is flagged and counts 0 until corrected.
          </p>
        </div>
      </header>

      {/* Controls */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4 mb-4 flex items-end gap-3 flex-wrap">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
          <input type="date" min={DATE_FLOOR} value={from} onChange={(e) => setFrom(e.target.value)}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
          <input type="date" min={DATE_FLOOR} value={to} onChange={(e) => setTo(e.target.value)}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
        </div>
        <button onClick={load} disabled={loading}
          className="bg-orange-500 text-white px-4 py-1.5 rounded-xl text-sm font-medium hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-1">
          <MagnifyingGlassIcon className="w-4 h-4" /> {loading ? 'Loading…' : 'Run preview'}
        </button>
        {data && (
          <button onClick={downloadCSV}
            className="ml-auto bg-emerald-500 text-white px-4 py-1.5 rounded-xl text-sm font-medium hover:bg-emerald-600 inline-flex items-center gap-1">
            <ArrowDownTrayIcon className="w-4 h-4" /> Download CSV
          </button>
        )}
      </div>

      {!data ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center text-sm text-gray-400">
          Pick a range and click "Run preview" to see hours.
        </div>
      ) : (
        <>
          {/* Summary header */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <SummaryCard label="Staff with hours" value={data.summary.total_staff} color="blue" />
            <SummaryCard label="Total hours" value={formatHours(data.summary.total_minutes)} color="gray" />
            <SummaryCard label="After 6 PM hours" value={formatHours(data.summary.after_6pm_minutes)} color="purple"
              sub={data.summary.after_6pm_minutes > 0 ? '+$1.50/hr differential' : null} />
            <SummaryCard label="Holiday hours" value={formatHours(data.summary.holiday_minutes)} color="emerald" />
          </div>

          {/* Per-staff table */}
          {data.staff.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center text-sm text-gray-400">
              No clocked hours found in this range. Staff hours come from actual clock-ins.
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-xs text-gray-500 uppercase bg-gray-50">
                    <th className="text-left px-4 py-2">Staff</th>
                    <th className="text-right px-4 py-2">Total</th>
                    <th className="text-right px-4 py-2">Until 6 PM</th>
                    <th className="text-right px-4 py-2">After 6 PM</th>
                    <th className="text-right px-4 py-2">Holiday</th>
                    <th className="px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {data.staff.map(s => {
                    const isOpen = expanded.has(s.staff_id);
                    return (
                      <Fragment key={s.staff_id}>
                        <tr className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => toggleStaff(s.staff_id)}>
                          <td className="px-4 py-2">
                            <div className="font-medium text-gray-900">
                              <StaffLink id={s.staff_id} name={s.name} />
                            </div>
                            <div className="text-[11px] text-gray-500 capitalize">{s.role?.replace(/_/g, ' ')}</div>
                          </td>
                          <td className="px-4 py-2 text-right font-semibold">{formatHours(s.total_minutes)}</td>
                          <td className="px-4 py-2 text-right text-gray-700">{formatHours(s.before_6pm_minutes)}</td>
                          <td className="px-4 py-2 text-right text-purple-700 font-medium">
                            {s.after_6pm_minutes > 0 ? formatHours(s.after_6pm_minutes) : '—'}
                          </td>
                          <td className="px-4 py-2 text-right text-emerald-700">
                            {s.holiday_minutes > 0 ? formatHours(s.holiday_minutes) : '—'}
                          </td>
                          <td className="px-2 py-2 text-right text-xs text-gray-400">
                            {isOpen ? '▼' : '▶'} {s.days.length} days
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="bg-gray-50/40">
                            <td colSpan={6} className="px-4 py-3">
                              <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="text-[11px] text-gray-500 uppercase">
                                      <th className="text-left px-2 py-1">Date</th>
                                      <th className="text-left px-2 py-1">Shift</th>
                                      <th className="text-left px-2 py-1">Lunch</th>
                                      <th className="text-right px-2 py-1">Total</th>
                                      <th className="text-right px-2 py-1">Until 6 PM</th>
                                      <th className="text-right px-2 py-1">After 6 PM</th>
                                      <th className="text-right px-2 py-1">Holiday</th>
                                      <th className="text-left px-2 py-1">Note</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {s.days.map((d, i) => (
                                      <tr key={i} className="border-t border-gray-100">
                                        <td className="px-2 py-1">
                                          <span className="text-gray-700">{d.day} </span>
                                          <span className="text-gray-500">{d.date}</span>
                                        </td>
                                        <td className="px-2 py-1 text-gray-600">
                                          {d.start && d.end ? `${d.start}–${d.end}` : '—'}
                                          {d.classroom && <span className="text-gray-400"> · {d.classroom}</span>}
                                        </td>
                                        <td className="px-2 py-1 text-gray-500">{d.lunch || '—'}</td>
                                        <td className="px-2 py-1 text-right font-medium">{formatHours(d.total_minutes)}</td>
                                        <td className="px-2 py-1 text-right">{formatHours(d.before_6pm_minutes)}</td>
                                        <td className="px-2 py-1 text-right text-purple-700">
                                          {d.after_6pm_minutes > 0 ? formatHours(d.after_6pm_minutes) : '—'}
                                        </td>
                                        <td className="px-2 py-1 text-right text-emerald-700">
                                          {d.holiday_minutes > 0 ? formatHours(d.holiday_minutes) : '—'}
                                          {d.holiday_name && <div className="text-[10px] text-emerald-600">{d.holiday_name}</div>}
                                        </td>
                                        <td className="px-2 py-1 text-gray-500 italic">{d.note || ''}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value, sub, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    gray: 'bg-gray-50 text-gray-700 border-gray-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  };
  return (
    <div className={`rounded-2xl border p-4 ${colors[color]}`}>
      <p className="text-xs font-medium opacity-75">{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
      {sub && <p className="text-[11px] opacity-60 mt-0.5">{sub}</p>}
    </div>
  );
}
